import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { useTransitionTimeLineChart } from "./hooks/useTransitionTimeLineChart";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useGlobalFilters } from "../../../stores/GlobalFilterContext/GlobalFilterContext";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, {
  TileChartContent,
  TileStatusContent,
} from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import TransitionTimeLineChartSettings from "./TransitionTimeLineChartSettings";
import * as Tooltip from "@radix-ui/react-tooltip";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
);

const GRANULARITY_LABELS = {
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
};

function formatGermanDateLabel(value) {
  const parsed = new Date(value + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Tooltip title — fully qualifies the bucket so the user can read date/year/range
 *  without depending on the (potentially scrolled-out) x-axis tick. Per-granularity:
 *    day   → "12:00 – 13:00 · 06. Dez. 2025"
 *    week  → "Mo., 06. Okt. 2025"
 *    month → "06. Okt. 2025"
 *    year  → "Januar 2025"
 */
function formatTooltipTitle(iso, granularity) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  if (granularity === "day") {
    const startH = String(d.getHours()).padStart(2, "0");
    const endH = String((d.getHours() + 1) % 24).padStart(2, "0");
    const datePart = d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${startH}:00 - ${endH}:00 | ${datePart}`;
  }
  if (granularity === "year") {
    return d.toLocaleDateString("de-DE", {
      month: "long",
      year: "numeric",
    });
  }
  if (granularity === "week") {
    return d.toLocaleDateString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  // month → day buckets
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** X-axis tick formatter matching AttributeSumLineChart / ResourceUtilizationChart.
 *  Day granularity uses hour buckets (unique to this tile), so it formats "HH:00";
 *  all other granularities mirror the de-DE locale used by other line/bar charts. */
function formatXAxisDateLabel(value, granularity) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  if (granularity === "day") {
    return `${String(parsed.getHours()).padStart(2, "0")}:00`;
  }
  if (granularity === "year") {
    return parsed.toLocaleDateString("de-DE", { month: "short" });
  }
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
  });
}

function fmtLocal(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function computeDisplayRange(dateStr, gran) {
  if (!dateStr) return { startDate: "", endDate: "" };
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return { startDate: "", endDate: "" };

  if (gran === "day") return { startDate: fmtLocal(d), endDate: fmtLocal(d) };

  if (gran === "week") {
    const day = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { startDate: fmtLocal(mon), endDate: fmtLocal(sun) };
  }

  if (gran === "month") {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { startDate: fmtLocal(start), endDate: fmtLocal(end) };
  }

  return {
    startDate: `${d.getFullYear()}-01-01`,
    endDate: `${d.getFullYear()}-12-31`,
  };
}

function defaultAnchor(gran) {
  const today = new Date();
  if (gran === "day") {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    return fmtLocal(y);
  }
  return fmtLocal(today);
}

/** Format a seconds value as a short, human-friendly duration. Picks the unit
 *  per-value — used for tooltips where each point reports its own magnitude. */
function formatSeconds(value) {
  const v = Number(value || 0);
  if (v < 60) return `${v.toFixed(v < 10 ? 1 : 0)} s`;
  if (v < 3600) return `${(v / 60).toFixed(1)} min`;
  return `${(v / 3600).toFixed(2)} h`;
}

/** Pick a single duration unit for the entire y-axis based on the largest
 *  non-null median, so all ticks share one unit (e.g. all "min" or all "h").
 *  Returns the divisor to apply to a seconds value, the suffix string, and
 *  how many decimals to render. */
function pickAxisUnit(maxSeconds) {
  const m = Number(maxSeconds || 0);
  if (m < 60) return { divisor: 1, suffix: "s", decimals: m < 10 ? 1 : 0 };
  if (m < 3600) return { divisor: 60, suffix: "min", decimals: 1 };
  return { divisor: 3600, suffix: "h", decimals: 2 };
}

/** Isolated date input — matches the pattern used by other tiles (uncontrolled, commits on
 *  pointer-pick, Enter, or blur) to avoid Chrome's section-cursor reset on keystroke. */
const DateInput = React.memo(function DateInput({ value, onChange, disabled }) {
  const committed = useRef(value);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const isPointerRef = useRef(false);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = value;
    committed.current = value;
  }, [value]);

  const commit = useCallback(() => {
    const val = inputRef.current?.value;
    if (!val || val === committed.current) return;
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(val) ||
      isNaN(new Date(val + "T00:00:00").getTime())
    )
      return;
    committed.current = val;
    onChangeRef.current(val);
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onPointerDown = () => {
      isPointerRef.current = true;
    };
    const onKeyDown = () => {
      isPointerRef.current = false;
    };
    const onNativeChange = () => {
      if (isPointerRef.current) {
        isPointerRef.current = false;
        commit();
      }
    };
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("keydown", onKeyDown);
    el.addEventListener("change", onNativeChange);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("keydown", onKeyDown);
      el.removeEventListener("change", onNativeChange);
    };
  }, [commit]);

  return (
    <input
      ref={inputRef}
      type="date"
      defaultValue={value}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
      }}
      onBlur={commit}
      disabled={disabled}
      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
    />
  );
});

function TransitionTimeLineChart({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) {
  const { t } = useTranslation();
  const {
    loading,
    error,
    chartData,
    datasetLastDate,
    objectTypes,
    availableResources,
    isMetadataLoaded,
    // TileConfig
    title,
    selectedObjectType,
    configuredResources,
    toleranceThreshold,
    enabledGranularities,
    // ViewState
    viewResources,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setConfiguredResources,
    setToleranceThreshold,
    setEnabledGranularities,
    // ViewState setters
    setViewResources,
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    // Actions
    fetchMetadata,
    fetchChartData,
  } = useTransitionTimeLineChart(activeFile, initialSettings);

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, selectedObjectType, configuredResources, toleranceThreshold,
      enabledGranularities, viewResources, granularity, selectedDate,
      viewMode, dateRangeStart, dateRangeEnd,
    }));
  }, [title, selectedObjectType, configuredResources, toleranceThreshold, // eslint-disable-line react-hooks/exhaustive-deps
      enabledGranularities, viewResources, granularity, selectedDate,
      viewMode, dateRangeStart, dateRangeEnd]);

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved title.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(title || "Transition Time Line Chart");
  }, [title]);

  const { filterState } = useGlobalFilters();
  const isFirstFilterRenderRef = useRef(true);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [viewResourceError, setViewResourceError] = useState("");
  const [viewDateError, setViewDateError] = useState("");
  const viewResourcesRef = useRef(viewResources);
  const resourceSelectionDirtyRef = useRef(false);

  useEffect(() => {
    viewResourcesRef.current = viewResources;
  }, [viewResources]);

  const displayRange = useMemo(
    () => computeDisplayRange(selectedDate, granularity),
    [selectedDate, granularity],
  );

  const handleOpenSettings = useCallback(() => {
    setIsSettingsModalOpen(true);
  }, []);

  const handleApplySettings = useCallback(
    async (values) => {
      const normalizedGranularity = values.enabledGranularities.includes(
        granularity,
      )
        ? granularity
        : values.enabledGranularities[0] || "day";

      const nextViewResources =
        values.configuredResources.length > 0
          ? (() => {
              const kept = viewResources.filter((r) =>
                values.configuredResources.includes(r),
              );
              return kept.length > 0 ? kept : values.configuredResources;
            })()
          : [];

      if (onTitleChange) onTitleChange(values.title);

      setTitle(values.title);
      setSelectedObjectType(values.selectedObjectType);
      setConfiguredResources(values.configuredResources);
      setToleranceThreshold(values.toleranceThreshold);
      setEnabledGranularities(values.enabledGranularities);
      setViewResources(nextViewResources);
      setGranularity(normalizedGranularity);

      await fetchChartData({
        ...values,
        granularity: normalizedGranularity,
        viewResources: nextViewResources,
        selectedDate,
      });
    },
    [
      fetchChartData,
      granularity,
      selectedDate,
      onTitleChange,
      viewResources,
      setConfiguredResources,
      setEnabledGranularities,
      setSelectedObjectType,
      setTitle,
      setToleranceThreshold,
      setGranularity,
      setViewResources,
    ],
  );

  // Re-fetch when global filters change
  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    fetchMetadata();
    if (
      configuredResources.length > 0 &&
      selectedObjectType &&
      viewResources.length > 0
    ) {
      fetchChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState]);

  useAutoLoad(fetchMetadata, [activeFile], isMetadataLoaded);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(async () => {
        if (isMetadataLoaded) await fetchChartData();
      });
    }
  }, [onRefresh, isMetadataLoaded, fetchChartData]);

  const headerActions = useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    ),
    [handleOpenSettings],
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  const triggerAutoApply = useCallback(
    (overrides = {}) => {
      const effectiveViewResources =
        overrides.viewResources ?? viewResourcesRef.current;
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart =
        overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;

      if (!effectiveViewResources || effectiveViewResources.length === 0) {
        setViewResourceError("Select at least one resource.");
        return false;
      }
      setViewResourceError("");

      if (effectiveViewMode === "daterange") {
        if (!effectiveDateRangeStart || !effectiveDateRangeEnd) {
          setViewDateError("Select start and end dates.");
          return false;
        }
        if (effectiveDateRangeStart > effectiveDateRangeEnd) {
          setViewDateError("Start date must be on or before end date.");
          return false;
        }
      }
      setViewDateError("");

      fetchChartData({
        viewResources: effectiveViewResources,
        viewMode: effectiveViewMode,
        dateRangeStart: effectiveDateRangeStart,
        dateRangeEnd: effectiveDateRangeEnd,
        ...overrides,
      });
      return true;
    },
    [fetchChartData, viewMode, dateRangeStart, dateRangeEnd],
  );

  const handleViewResourcesChange = useCallback(
    (nextResources) => {
      resourceSelectionDirtyRef.current = true;
      viewResourcesRef.current = nextResources;
      setViewResources(nextResources);
      setViewResourceError(
        nextResources.length > 0 ? "" : "Select at least one resource.",
      );
    },
    [setViewResources],
  );

  const handleResourceDropdownOpenChange = useCallback(
    (isOpen) => {
      if (isOpen || !resourceSelectionDirtyRef.current) return;
      resourceSelectionDirtyRef.current = false;
      if (viewMode !== "granularity") return;
      triggerAutoApply({ viewResources: viewResourcesRef.current });
    },
    [viewMode, triggerAutoApply],
  );

  const handleGranularityChange = useCallback(
    (g) => {
      setGranularity(g);
      const baseAnchor = selectedDate || defaultAnchor(g);
      const normalizedAnchor =
        datasetLastDate && baseAnchor > datasetLastDate
          ? datasetLastDate
          : baseAnchor;
      setSelectedDate(normalizedAnchor);
      triggerAutoApply({ granularity: g, selectedDate: normalizedAnchor });
    },
    [
      setGranularity,
      setSelectedDate,
      selectedDate,
      datasetLastDate,
      triggerAutoApply,
    ],
  );

  const handleViewModeChange = useCallback(
    (nextMode) => {
      if (nextMode === viewMode) return;
      if (nextMode !== "daterange") {
        const baseAnchor =
          dateRangeStart || selectedDate || defaultAnchor(granularity);
        const normalizedAnchor =
          datasetLastDate && baseAnchor > datasetLastDate
            ? datasetLastDate
            : baseAnchor;
        setSelectedDate(normalizedAnchor);
      }
      setViewMode(nextMode);
    },
    [
      viewMode,
      selectedDate,
      granularity,
      dateRangeStart,
      datasetLastDate,
      setSelectedDate,
      setViewMode,
    ],
  );

  const handleSelectedDateCommit = useCallback(
    (nextDate) => {
      setSelectedDate(nextDate);
      if (viewMode === "granularity") {
        triggerAutoApply({ viewMode: "granularity", selectedDate: nextDate });
      }
    },
    [setSelectedDate, viewMode, triggerAutoApply],
  );

  const handleDateRangeStartCommit = useCallback(
    (nextDate) => {
      setDateRangeStart(nextDate);
      setViewDateError("");
    },
    [setDateRangeStart],
  );

  const handleDateRangeEndCommit = useCallback(
    (nextDate) => {
      setDateRangeEnd(nextDate);
      setViewDateError("");
    },
    [setDateRangeEnd],
  );

  const handleApplyDateRange = useCallback(() => {
    triggerAutoApply({ viewMode: "daterange" });
  }, [triggerAutoApply]);

  const handleDateRangePresetChange = useCallback(
    (value) => {
      const start = (value?.startDate || "").slice(0, 10);
      const end = (value?.endDate || "").slice(0, 10);
      setDateRangeStart(start);
      setDateRangeEnd(end);
      setViewMode("daterange");
      setViewDateError("");
      triggerAutoApply({ viewMode: "daterange", dateRangeStart: start, dateRangeEnd: end });
    },
    [setDateRangeStart, setDateRangeEnd, setViewMode, triggerAutoApply],
  );

  const buckets = chartData?.buckets || [];
  const bucketGranularity = chartData?.granularity || granularity;
  const hasAnyData = buckets.some((b) => (b.count ?? 0) > 0);
  const axisUnit = useMemo(() => {
    let max = 0;
    for (const b of buckets) {
      const v = b.median_transition_time_seconds;
      if (v != null && v > max) max = v;
    }
    return pickAxisUnit(max);
  }, [buckets]);

  const chartjsData = useMemo(() => {
    if (!buckets.length) return null;
    // Empty positions render as 0 to preserve the original continuous-line
    // convention used by this tile before the null-gap rendering change.
    return {
      labels: buckets.map((b) =>
        formatXAxisDateLabel(b.bucket_start, bucketGranularity),
      ),
      datasets: [
        {
          label: t('tiles:transition.legend'),
          data: buckets.map((b) =>
            (b.count ?? 0) > 0 ? b.median_transition_time_seconds : 0,
          ),
          borderColor: "rgba(59, 130, 246, 1)",
          backgroundColor: "rgba(59, 130, 246, 0.7)",
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          tension: 0.1,
          fill: false,
        },
      ],
    };
  }, [buckets, bucketGranularity]);

  const chartjsOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "top",
          labels: {
            color: "#6b7280",
            font: { size: 11 },
            padding: 12,
            usePointStyle: false,
            boxWidth: 28,
            boxHeight: 2,
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (context) => {
              const idx = context.dataIndex;
              const bucket = buckets[idx];
              const count = bucket?.count ?? 0;
              if (count === 0) {
                return "No transitions in this bucket";
              }
              const seconds = context.parsed.y;
              return [
                `Median: ${formatSeconds(seconds)}`,
                `Transitions counted: ${count}`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          offset: false,
          title: {
            display: true,
            // Make the bucket-start convention explicit — e.g. for Day granularity
            // a tick labeled "16:00" represents the hour 16:00–16:59, not a point at 4 PM.
            text:
              bucketGranularity === "day"
                ? "Hour"
                : bucketGranularity === "year"
                  ? "Month"
                  : "Day",
            color: "#4b5563",
            font: { size: 12 },
          },
          ticks: { maxRotation: 45, color: "#6b7280", font: { size: 11 } },
        },
        y: {
          title: {
            display: true,
            text: "Median transition time",
            color: "#4b5563",
            font: { size: 12 },
          },
          ticks: {
            color: "#6b7280",
            font: { size: 11 },
            // All ticks share the unit picked from the data's max value, so the
            // axis stays in one denomination (s / min / h) instead of mixing.
            callback: (value) =>
              `${(Number(value) / axisUnit.divisor).toFixed(axisUnit.decimals)} ${axisUnit.suffix}`,
          },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
    }),
    [buckets, bucketGranularity, axisUnit],
  );

  const totalTransitionsConsidered = Number(
    chartData?.total_transitions_considered ?? 0,
  );
  const totalTransitionsKept = Number(chartData?.total_transitions_kept ?? 0);
  const showLoadDataPrompt = !isMetadataLoaded && !loading && !error;
  const showConfigPrompt =
    isMetadataLoaded && !loading && !error && configuredResources.length === 0;
  const showContent =
    isMetadataLoaded && !error && configuredResources.length > 0;
  const showChart = showContent && chartData !== null && hasAnyData;
  const showNoDataInPeriod =
    showContent &&
    !loading &&
    chartData !== null &&
    totalTransitionsConsidered === 0;
  const showNoTransitionsBelowThreshold =
    showContent &&
    !loading &&
    chartData !== null &&
    totalTransitionsConsidered > 0 &&
    totalTransitionsKept === 0;
  const showSelectionPrompt = showContent && !loading && chartData === null;

  useEffect(() => {
    if (!showContent || loading || error || chartData !== null) return;
    if (viewResources.length === 0) return;
    if (viewMode !== "granularity") return;
    triggerAutoApply();
  }, [
    showContent,
    loading,
    error,
    chartData,
    viewResources,
    viewMode,
    triggerAutoApply,
  ]);

  return (
    <BaseTile title={title} loading={loading} minHeight="lg" className="h-full">
      <TileContent layout="flex" direction="column" gap="md" grow>
        {showLoadDataPrompt && (
          <TileStatusContent
            status="info"
            title="Load Resources Data"
            message="Load metadata first to configure available resources."
            action={
              <button
                onClick={fetchMetadata}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Load Resources Data
              </button>
            }
          />
        )}

        {showConfigPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Transition Time Chart"
            message="Open settings to configure resources, shift, threshold and granularities."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Configure Chart
              </button>
            }
          />
        )}

        {showContent && (
          <>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3 no-drag">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="sm:w-96">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Resources
                  </label>
                  <MultiSelectDropdown
                    options={configuredResources}
                    selectedValues={viewResources}
                    onSelectionChange={handleViewResourcesChange}
                    onOpenChange={handleResourceDropdownOpenChange}
                    placeholder="Select resources..."
                    itemType="resources"
                    className="text-sm w-full"
                    minOptionWidth="0"
                    explicitDisplayCount={5}
                  />
                  {viewResourceError && (
                    <p className="mt-1 text-xs text-error-600">
                      {viewResourceError}
                    </p>
                  )}
                </div>
                <div className="flex gap-0.5 bg-gray-200 rounded-md p-0.5">
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("granularity")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "granularity"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Granularity
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("daterange")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "daterange"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Date Range
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    Time Period
                  </label>
                  <Tooltip.Provider delayDuration={200}>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <button
                          type="button"
                          className="inline-flex text-gray-400 hover:text-gray-500 transition-colors"
                          aria-label="Time period help"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          side="top"
                          align="start"
                          sideOffset={6}
                          className="max-w-[240px] bg-white border border-gray-200 rounded-md shadow-lg z-50"
                        >
                          <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
                            <span className="text-xs font-medium text-primary-700">
                              Median transition time
                            </span>
                          </div>
                          <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
                            Shows the <b>median</b> transition time per bucket.
                            Only transitions <b>strictly below</b> the
                            configured threshold are plotted. Buckets with no
                            kept transitions show no point.
                          </div>
                          <Tooltip.Arrow className="fill-white" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                </div>
              </div>

              {viewMode === "granularity" ? (
                <div className="flex flex-wrap items-center gap-2">
                  {enabledGranularities.map((g) => {
                    const active = granularity === g;
                    return (
                      <button
                        key={g}
                        type="button"
                        onClick={() => handleGranularityChange(g)}
                        className={`px-3 py-1.5 rounded-md border text-sm ${
                          active
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        {GRANULARITY_LABELS[g] || g}
                      </button>
                    );
                  })}
                  <div className="flex items-center gap-3">
                    <DateInput
                      value={selectedDate}
                      onChange={handleSelectedDateCommit}
                      disabled={loading}
                    />
                    <span className="text-xs text-gray-500">
                      {chartData?.period_label ||
                        `${formatGermanDateLabel(displayRange.startDate)} - ${formatGermanDateLabel(displayRange.endDate)}`}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <DateInput
                      value={dateRangeStart}
                      onChange={handleDateRangeStartCommit}
                      disabled={loading}
                    />
                    <span className="text-xs text-gray-500">to</span>
                    <DateInput
                      value={dateRangeEnd}
                      onChange={handleDateRangeEndCommit}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={handleApplyDateRange}
                      disabled={loading || viewResources.length === 0}
                      className="px-4 py-1.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? "Loading..." : "Apply"}
                    </button>
                  </div>
                  {viewDateError && (
                    <p className="text-xs text-error-600">{viewDateError}</p>
                  )}
                </div>
              )}
            </div>

            {showChart && (
              <TileChartContent height={440} className="flex-grow">
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <Line data={chartjsData} options={chartjsOptions} />
                </div>
              </TileChartContent>
            )}

            {showNoDataInPeriod && (
              <TileStatusContent
                status="empty"
                title="No data in selected period"
                message="No transitions were found for the selected resources and time period. Pick a different period or resource selection."
                icon={
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                }
              />
            )}

            {showNoTransitionsBelowThreshold && (
              <TileStatusContent
                status="empty"
                title="No transitions below threshold"
                message="No transitions in the selected period fell strictly below the configured threshold. Adjust the threshold in Settings or pick a different period."
                icon={
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                }
              />
            )}

            {showSelectionPrompt && (
              <TileStatusContent
                status="empty"
                title="Select Filters to Load Chart"
                message={
                  viewMode === "granularity"
                    ? "Granularity mode applies automatically: granularity/date on commit and resources on dropdown close."
                    : "Date Range mode requires clicking Apply after selecting resources and dates."
                }
              />
            )}
          </>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load chart"
            message={typeof error === "string" ? error : "Unexpected error."}
            action={
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (isMetadataLoaded) fetchChartData();
                    else fetchMetadata();
                  }}
                  className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
                >
                  Retry
                </button>
                <button
                  onClick={handleOpenSettings}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Open Settings
                </button>
              </div>
            }
          />
        )}
      </TileContent>

      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        title="Transition Time Line Chart Settings"
        getInitialValues={() => ({
          title,
          selectedObjectType,
          configuredResources: [...configuredResources],
          toleranceThreshold,
          enabledGranularities: [...enabledGranularities],
        })}
        onApply={handleApplySettings}
        isApplyDisabled={(values) =>
          !(
            isMetadataLoaded &&
            values.selectedObjectType &&
            values.configuredResources?.length > 0 &&
            selectedDate &&
            values.enabledGranularities?.length > 0
          ) || loading
        }
        applyButtonText={loading ? "Calculating..." : "Apply and Calculate"}
      >
        {({ values, setField }) => (
          <TransitionTimeLineChartSettings
            title={values.title}
            objectTypes={objectTypes}
            selectedObjectType={values.selectedObjectType}
            availableResources={availableResources}
            configuredResources={values.configuredResources}
            toleranceThreshold={values.toleranceThreshold}
            enabledGranularities={values.enabledGranularities}
            onTitleChange={(v) => setField("title", v)}
            onObjectTypeChange={(v) => setField("selectedObjectType", v)}
            onConfiguredResourcesChange={(v) =>
              setField("configuredResources", v)
            }
            onToleranceThresholdChange={(v) =>
              setField("toleranceThreshold", v)
            }
            onEnabledGranularitiesChange={(v) =>
              setField("enabledGranularities", v)
            }
            dateRangeStart={dateRangeStart}
            dateRangeEnd={dateRangeEnd}
            onDateRangePresetChange={handleDateRangePresetChange}
            loading={loading}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
}

export default TransitionTimeLineChart;
